import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MessageMedium } from 'modules/graphql/types';
import { Button, BUTTON_SIZES } from 'ui/button';
import { InputText } from 'ui/inputs';
import { FormField } from 'ui/formField';
import { ModalFooter, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { isValidEmail } from 'utils/stringHelpers';

import { selectUserProjectIsUuidBased } from 'core/userContext';
import { displayContent } from 'modules/templateEditor/common';
import {
  useSendTestMsgContext,
  TestMsgModals,
} from 'modules/templateEditor/context/SendTestMsgContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { addNegativeToast } from 'modules/toastContainer';
import { hasOnlyValidEmails, cleanRecipients, validateUserIds } from './SendTestMsgToOther.utils';
import { StyledModal, StyledModalBody, StyledModalSubheader } from '../SendTestMsgModals.styled';

export const SendTestMsgToOtherModal = () => {
  const { onSendTestToOtherEmail, onSendTestToOtherUserId, setOpenTestMsgModal, isSendingTestMsg } =
    useSendTestMsgContext();
  const { medium } = useTemplateQueryContext();
  const [recipientList, setRecipientList] = useState<string>('');
  const isUuidBased = useSelector(selectUserProjectIsUuidBased);
  const dispatch = useDispatch();

  const handleCloseModal = () => {
    setOpenTestMsgModal(TestMsgModals.None);
  };

  const handleSendTestMsg = async () => {
    // Clean the user input, then separate into email and non-email
    const recipients = cleanRecipients(recipientList);
    const validEmails = recipients.filter(isValidEmail);
    const invalidEmails = recipients.filter((recipient) => !isValidEmail(recipient));

    if (invalidEmails.length) {
      if (isUuidBased) {
        // If the project is Uuid-based, any entries that are not emails may be userIds
        const { validUserIds, invalidUserIdResponses } = await validateUserIds(invalidEmails);

        // If any are valid userIds, send test message to those userIds
        if (validUserIds.length) onSendTestToOtherUserId(validUserIds);

        // If they are not valid userIds, show an error toast indicating the error
        if (invalidUserIdResponses.length) {
          if (invalidUserIdResponses.find((res) => res.status === 403)) {
            dispatch(
              addNegativeToast(`You do not have the necessary permissions to send to a UUID`),
            );
          } else {
            // 404 (uuid not found)
            const failedUuids = invalidUserIdResponses.map((r) => r.uuid);
            dispatch(addNegativeToast(`Request failed with userId(s): ${failedUuids.join(', ')}`));
          }
        }
      } else {
        // If the project is not Uuid-based, any inputs that are not valid emails are simply invalid
        dispatch(addNegativeToast(`Invalid recipients: ${invalidEmails.join(', ')}`));
      }
    }

    if (validEmails.length) onSendTestToOtherEmail(validEmails);

    // close the modal last (this component will be unmounted)
    // Timeout avoids console warning "Form submission canceled because the form is not connected"
    setTimeout(handleCloseModal, 100);
  };

  return (
    <StyledModal
      id="sendTestMsgToOtherModal"
      isModalOpen={true}
      onClose={handleCloseModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>
        {displayContent.strSendTestToOtherTitle({ isEmail: medium === MessageMedium.Email })}
      </ModalHeader>
      <StyledModalSubheader>
        {displayContent.strSendTestToOtherDesc({ isEmail: medium === MessageMedium.Email })}
      </StyledModalSubheader>
      <StyledModalBody>
        <form
          id="form-test-msg-to-other"
          // method controls what happens when the form is submitted.
          // setting it to dialog is a hack to make sure the autocomplete on the input still works,
          // because the autocomplete will only be populated when the form action completes.
          // see https://iterable.atlassian.net/browse/TNT-5428 for more context
          method="dialog"
          onSubmit={handleSendTestMsg}
        >
          <FormField
            helpText={
              isUuidBased
                ? displayContent.strRecipientsHelpTextUuid
                : displayContent.strRecipientsHelpText
            }
            labelText={displayContent.labelRecipients}
          >
            <InputText
              autoComplete={'on'}
              data-test="input-test-msg-recipient-list"
              id="input-test-msg-recipient-list"
              onChange={setRecipientList}
              placeholder={
                isUuidBased
                  ? displayContent.placeholderRecipientsForUUID
                  : displayContent.placeholderRecipients
              }
              value={recipientList}
              width="100%"
              autoFocus
            />
          </FormField>
        </form>
      </StyledModalBody>

      <ModalFooter>
        <Button
          isLoading={isSendingTestMsg}
          form="form-test-msg-to-other"
          isDisabled={isUuidBased ? recipientList.length === 0 : !hasOnlyValidEmails(recipientList)}
          size={BUTTON_SIZES.LARGE}
          type="submit"
        >
          {displayContent.btnSend({ isEmail: medium === MessageMedium.Email })}
        </Button>
      </ModalFooter>
    </StyledModal>
  );
};
